import {
  Box,
  Button,
  Checkbox,
  Chip,
  Stack,
  TextField,
  Tooltip,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { useMemo } from "react";
import {
  DeleteModal,
  InputControl,
  LimitedTag,
  PageListLayout,
  ServerPaginatedtable,
  SimpleMenu,
} from "../../../shared";
import AddIcon from "@mui/icons-material/Add";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { useIngredients } from "../hooks/UseIngredient";

export const IngredientsList = () => {
  const {
    listQuery: { isLoading, data },
    listQuery,
    // ingredient,
    deleteInfo,
    onDelete,
    resetDeleteInfo,
    confirmDelete,
    onSearchChange,
    setPagination,
    pagination,
    sorting,
    setSorting,
    selectStatus,
    disableclearButton,
    onClearFilter,
    globalData,
    typeValuesHandler,
    optionsTagsList,
    tagValuesHandler,
    isCreateVisible,
    isEditable,
    isDeletable,
    tableData,
    moduleName,
    search,
    optionsSupplierList,
    supplierValuesHandler,
  } = useIngredients({
    isIngredientlist: true,
    isTaxgrouplist: false,
    isTaglist: true,
    isList: true,
  });

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.label,
  });
  const navigate = useNavigate();
  const columnHelper = createColumnHelper(data?.ingredient ?? []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Ingredient Name",
      }),
      columnHelper.accessor("tax_group_name", {
        header: "Tax Groups",
      }),
      columnHelper.accessor("type", {
        header: "Type",
        cell: (info) => {
          const typeValue = info.getValue()?.toLowerCase();
          return (
            globalData?.Ingredient_types?.find(
              (incType) => incType.value === typeValue,
            )?.label || "-"
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: ({ row }) => {
          const isStatusActive = row?.original?.status;

          return (
            <Chip
              size="small"
              color={isStatusActive ? "primary" : "gray"}
              variant="filled"
              label={isStatusActive ? "ACTIVE" : "INACTIVE"}
            />
          );
        },
      }),
      columnHelper.accessor("suppliers", {
        header: "Suppliers",
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {row?.original?.suppliers?.slice(0, 1).map((data) => (
                <Chip
                  size="small"
                  variant="outlined"
                  color="gray"
                  label={data}
                />
              ))}
              {row?.original?.suppliers?.length > 1 && (
                <Tooltip
                  placement="bottom"
                  arrow
                  title={
                    <>
                      {row?.original?.suppliers
                        ?.slice(1, row?.original?.suppliers?.length)
                        .map((val, index) => (
                          <div key={index}>{val}</div>
                        ))}
                    </>
                  }
                >
                  <Chip
                    label={`+${row?.original?.suppliers?.length - 1} More`}
                    variant="more"
                    size="small"
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      }),
      columnHelper.accessor("tags", {
        header: "Category Tags",
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {row?.original?.tags?.slice(0, 6).map((data) => (
                <Chip
                  size="small"
                  variant="outlined"
                  color="gray"
                  label={data}
                />
              ))}
              {row?.original?.tags?.length > 6 && (
                <Tooltip
                  placement="bottom"
                  arrow
                  title={
                    <>
                      {row?.original?.tags
                        ?.slice(6, row?.original?.tags?.length)
                        .map((val, index) => (
                          <div key={index}>{val}</div>
                        ))}
                    </>
                  }
                >
                  <Chip
                    sx={{
                      borderRadius: "15px",
                      cursor: "pointer",
                    }}
                    label={`+${row?.original?.tags?.length - 6} More`}
                    variant="more"
                    size="small"
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      }),

      columnHelper.display({
        id: "actions",
        cell: (props) => {
          return (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap="8px"
            >
              {(isDeletable || isEditable) && (
                <SimpleMenu
                  row={props.row}
                  editAction={(e) => navigate(`edit/${e.id}`)}
                  handleDeleteModal={(e) => {
                    onDelete(e.id);
                  }}
                  isEditable={isEditable}
                  isDeletable={isDeletable}
                />
              )}
              <img
                alt="details"
                src="/icons/arrow_forward.png"
                onClick={(e) =>
                  navigate(`/ingredients/details/${props.row.original.id}`)
                }
              />
            </Stack>
          );
        },
      }),
    ],
    [listQuery?.data?.ingredient, globalData?.Ingredient_types],
  );

  const breadcrumbs = [
    <Typography key="1" sx={{ fontSize: "14px", fontWeight: 400 }}>
      Ingredients
    </Typography>,
  ];

  return (
    <PageListLayout breadcrumbs={breadcrumbs} sx={{ p: 0 }}>
      <ServerPaginatedtable
        defaultData={listQuery?.data?.ingredients ?? []}
        columns={columns}
        search={search}
        onSearchChange={onSearchChange}
        sorting={sorting}
        setSorting={(callback) =>
          setSorting({ tableName: moduleName, callback })
        }
        pagination={pagination}
        paginationProps={
          listQuery.data?.pagination || { total: 0, lastPage: 1 }
        }
        setPagination={(callback) =>
          setPagination({ tableName: moduleName, callback })
        }
        headerText="Ingredients"
        isLoading={isLoading}
        onRowLink={(e) => {
          return `details/${e.id}`;
        }}
        tableHeaderContent={
          <Stack
            sx={{
              flexDirection: "row",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              multiple={true}
              options={globalData?.Ingredient_types ?? []}
              value={tableData.ingredients.data.types || []}
              disableCloseOnSelect
              disableClearable={true}
              onChange={(event, option) => typeValuesHandler(event, option)}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderTags={(value) => (
                <LimitedTag
                  value={value}
                  labelKey="label"
                  autocompleteWidth="230px"
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              )}
              filterOptions={filterOptions}
              sx={{ width: "230px" }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Type" />
              )}
            />
            <InputControl
              type="dropdown"
              variant="filter"
              size="small"
              options={selectStatus.statusOptions}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{
                width: "180px",
                height: "36px",
              }}
              value={
                selectStatus?.statusOptions?.find(
                  (option) =>
                    option.id === tableData.ingredients.data.selectedStatus,
                ) || null
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Status" />
              )}
              onChange={(event, option) =>
                selectStatus.statusValueChangeHandler(option)
              }
            />
            <InputControl
              type="dropdown"
              multiple={true}
              variant="filter"
              size="small"
              options={optionsSupplierList ?? []}
              value={tableData.ingredients.data.supplierlist || []}
              disableCloseOnSelect
              disableClearable={true}
              onChange={(event, option) => supplierValuesHandler(event, option)}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderTags={(value) => (
                <LimitedTag
                  value={value}
                  labelKey="label"
                  autocompleteWidth="230px"
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              )}
              filterOptions={filterOptions}
              sx={{ width: "230px" }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Suppliers" />
              )}
            />
            <InputControl
              type="dropdown"
              multiple={true}
              variant="filter"
              size="small"
              options={optionsTagsList ?? []}
              value={tableData.ingredients.data.tagslist || []}
              disableCloseOnSelect
              disableClearable={true}
              onChange={(event, option) => tagValuesHandler(event, option)}
              getOptionLabel={(option) => option.label || ""}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              renderTags={(value) => (
                <LimitedTag
                  value={value}
                  labelKey="label"
                  autocompleteWidth="230px"
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.label}
                </li>
              )}
              filterOptions={filterOptions}
              sx={{ width: "230px" }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Category Tags" />
              )}
            />
            <Button
              type="button"
              size="small"
              variant="text"
              disabled={disableclearButton}
              onClick={onClearFilter}
            >
              Clear All
            </Button>
          </Stack>
        }
        rightContent={
          isCreateVisible && (
            <Button
              type="button"
              size="medium"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("create")}
            >
              Ingredient
            </Button>
          )
        }
      />
      <DeleteModal
        open={deleteInfo.show}
        handleClose={() => resetDeleteInfo()}
        onConfirm={() => confirmDelete()}
      />
    </PageListLayout>
  );
};
